/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import LocalStorageService from "../../services/LocalStorageService";
import {
  getCreateUserRoute,
  getCurrentUserRoute,
  getDeleteUserRoute,
  getUserByIdRoute,
  getUsersPageableSearchRoute,
  getUsersRoute,
} from "../../lib/routes/security";

const initialState = {
  currentUser: {},
  currentUserLoading: rtkRequestStatusCodes.idle,
  users: [],
  user: {},
  usersLoading: rtkRequestStatusCodes.idle,
  userCreating: rtkRequestStatusCodes.idle,
  userDeleting: rtkRequestStatusCodes.idle,
  userLoading: rtkRequestStatusCodes.idle,
  pageableSearchUserLoading: rtkRequestStatusCodes.idle,
  totalUsers: 0,
};

const fetchUsers = createAsyncThunk("users/fetchAll", async ({ page }) => {
  const response = axios.get(getUsersRoute(page));
  const { data } = await response;
  return data;
});

const createUser = createAsyncThunk(
  "users/createUser",
  async ({ login, password, firstName, middleName, lastName, email }) => {
    const response = axios.post(getCreateUserRoute(), {
      login,
      password,
      tenantId: LocalStorageService.getTenantId(),
      userType: "internal",
      userInfo: {
        firstName,
        middleName,
        lastName,
        email,
      },
    });
    const { data } = await response;
    return data;
  }
);

const deleteUser = createAsyncThunk("users/deleteUser", async (userId) => {
  axios.delete(getDeleteUserRoute(userId));
});

const pageableSearchUser = createAsyncThunk(
  "users/pageableSearchUser",
  async ({ lastName, firstName, login, email }) => {
    const response = axios.get(
      getUsersPageableSearchRoute({
        lastName,
        firstName,
        login,
        email,
      })
    );
    const { data } = await response;
    return data;
  }
);

const getUserById = createAsyncThunk("users/getUserById", async (userId) => {
  const response = axios.get(getUserByIdRoute(userId));
  const { data } = await response;
  return data;
});

const fetchCurrentUser = createAsyncThunk("users/getCurrentUser", async () => {
  const response = axios.get(getCurrentUserRoute());
  const { data } = await response;
  return data;
});

export const usersSlice = createSlice({
  name: "users",
  initialState,
  extraReducers: {
    [fetchUsers.fulfilled]: (state, action) => {
      state.users = action.payload.content;
      state.usersLoading = rtkRequestStatusCodes.fulfilled;
      state.totalUsers = action.payload.totalElements;
    },
    [fetchUsers.pending]: (state) => {
      state.usersLoading = rtkRequestStatusCodes.pending;
    },
    [fetchUsers.rejected]: (state) => {
      state.usersLoading = rtkRequestStatusCodes.rejected;
    },
    [createUser.fulfilled]: (state) => {
      state.userCreating = rtkRequestStatusCodes.fulfilled;
    },
    [createUser.pending]: (state) => {
      state.userCreating = rtkRequestStatusCodes.pending;
    },
    [createUser.rejected]: (state) => {
      state.userCreating = rtkRequestStatusCodes.rejected;
    },
    [deleteUser.fulfilled]: (state) => {
      state.userDeleting = rtkRequestStatusCodes.fulfilled;
    },
    [deleteUser.pending]: (state) => {
      state.userDeleting = rtkRequestStatusCodes.pending;
    },
    [deleteUser.rejected]: (state) => {
      state.userDeleting = rtkRequestStatusCodes.rejected;
    },
    [pageableSearchUser.fulfilled]: (state, action) => {
      state.users = action.payload.content;
      state.pageableSearchUserLoading = rtkRequestStatusCodes.fulfilled;
      state.totalUsers = action.payload.totalElements;
    },
    [pageableSearchUser.pending]: (state) => {
      state.pageableSearchUserLoading = rtkRequestStatusCodes.pending;
    },
    [pageableSearchUser.rejected]: (state) => {
      state.pageableSearchUserLoading = rtkRequestStatusCodes.rejected;
    },
    [getUserById.fulfilled]: (state, action) => {
      state.user = action.payload;
      state.userLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getUserById.pending]: (state) => {
      state.userLoading = rtkRequestStatusCodes.pending;
    },
    [getUserById.rejected]: (state) => {
      state.userLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchCurrentUser.fulfilled]: (state, action) => {
      state.currentUser = action.payload;
      state.currentUserLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchCurrentUser.pending]: (state) => {
      state.currentUserLoading = rtkRequestStatusCodes.pending;
    },
    [fetchCurrentUser.rejected]: (state) => {
      state.currentUserLoading = rtkRequestStatusCodes.rejected;
    },
  },
});

const { reducer } = usersSlice;

export {
  fetchUsers,
  createUser,
  deleteUser,
  pageableSearchUser,
  getUserById,
  fetchCurrentUser,
  reducer,
};
